import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../contexts/AuthContext';
import SelectLocations from './steps/SelectLocations';
import ChooseCampaignType from './steps/ChooseCampaignType';
import ConfigureStopLogic from './steps/ConfigureStopLogic';
import SelectStops from './steps/SelectStops';
import ConfigureRouteLogic from './steps/ConfigureRouteLogic';
import SelectRoutes from './steps/SelectRoutes';
import ConfigureProximityIQ from './steps/ConfigureProximityIQ';
import ChooseCampaignChannel from './steps/ChooseCampaignChannel';
import SelectAudienceMethod from './steps/SelectAudienceMethod';
import SelectTargetSegments from './steps/SelectTargetSegments';
import BuildCustomAudience from './steps/BuildCustomAudience';
import SMSTemplateDelivery from './steps/SMSTemplateDelivery';
import EmailTemplateDelivery from './steps/EmailTemplateDelivery';
import ReviewAndLaunch from './steps/ReviewAndLaunch';
import ChooseAdPlatform from './steps/ChooseAdPlatform';
import ConfigureGoogleAds from './steps/ConfigureGoogleAds';
// New Meta Ads Flow Components
import ChooseCampaignObjective from './steps/ChooseCampaignObjective';
import MetaCampaignSettings from './steps/MetaCampaignSettings';
import MetaCampaignBudgetBid from './steps/MetaCampaignBudgetBid';
import MetaAdSetSettings from './steps/MetaAdSetSettings';
import MetaAdSetBudgetSchedule from './steps/MetaAdSetBudgetSchedule';
import MetaAudienceTargeting from './steps/MetaAudienceTargeting';
import MetaPlacements from './steps/MetaPlacements';
import MetaAdFormatMedia from './steps/MetaAdFormatMedia';
import MetaDestinationAdCopy from './steps/MetaDestinationAdCopy';
import MetaReviewLaunch from './steps/MetaReviewLaunch';
import './NewCampaignUX.css';

const NewCampaignUX = ({ campaignData: initialCampaignData, onUpdate: externalOnUpdate, onSave, isEditing = false, campaignId, singlePage = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isClient = currentUser?.role === 'client';
  const [currentStep, setCurrentStep] = useState(1);

  // Use provided campaignData or default state
  const [campaignData, setCampaignData] = useState(initialCampaignData || {
    selectedLocations: [],
    campaignType: null, // 'proximity', 'route', 'stop'
    // StopLogic specific
    selectedVehicles: [],
    dateRange: null,
    stopDuration: { min: 5, max: 720 },
    targetingRadius: 0.5,
    selectedStops: [],
    // RouteLogic specific
    breadcrumbSpacing: null,
    selectedRoutes: [],
    // ProximityIQ specific
    notificationRange: 1000,
    campaignChannel: null, // 'sms', 'email', 'both'
    audienceMethod: null, // 'quick', 'custom'
    selectedSegments: [],
    customFilters: { service: [], geo: [] },
    // SMS Template & Delivery
    campaignName: '',
    smsMessage: '',
    cooldownPeriod: '',
    quietHoursEnabled: true,
    quietHoursFrom: '9:00 PM',
    quietHoursTo: '8:00 AM',
    // Ad platform
    adPlatform: null, // 'meta', 'google', 'both'
  });

  const getTotalSteps = () => {
    if (campaignData.campaignType === 'stop') {
      // Stop: 1-Location, 2-Type, 3-ConfigureStop, 4-SelectStops, 5-ChoosePlatform
      // Meta: 6-Objective, 7-CampaignSettings, 8-CampaignBudgetBid, 9-AdSetSettings, 10-AdSetBudgetSchedule (if Advantage+ OFF), 11-Audience, 12-Placements, 13-AdFormat, 14-AdCopy, 15-Review
      // Google: 6-ConfigureGoogle, 7-Review
      // Both: Meta steps + ConfigureGoogle + Review
      if (campaignData.adPlatform === 'both') {
        // Meta steps (14 or 15) + Google (1 step)
        return campaignData.metaAds?.advantagePlusBudget ? 15 : 16; // 15 if skipping AdSetBudgetSchedule
      }
      if (campaignData.adPlatform === 'meta') {
        return campaignData.metaAds?.advantagePlusBudget ? 14 : 15; // 14 if skipping AdSetBudgetSchedule
      }
      if (campaignData.adPlatform === 'google') return 7;
      return 5; // Before platform selection
    }
    if (campaignData.campaignType === 'route') {
      // Route: 1-Location, 2-Type, 3-ConfigureRoute, 4-SelectRoutes, 5-ChoosePlatform
      // Meta: 6-Objective, 7-CampaignSettings, 8-CampaignBudgetBid, 9-AdSetSettings, 10-AdSetBudgetSchedule (if Advantage+ OFF), 11-Audience, 12-Placements, 13-AdFormat, 14-AdCopy, 15-Review
      // Google: 6-ConfigureGoogle, 7-Review
      // Both: Meta steps + ConfigureGoogle + Review
      if (campaignData.adPlatform === 'both') {
        return campaignData.metaAds?.advantagePlusBudget ? 15 : 16;
      }
      if (campaignData.adPlatform === 'meta') {
        return campaignData.metaAds?.advantagePlusBudget ? 14 : 15;
      }
      if (campaignData.adPlatform === 'google') return 7;
      return 5; // Before platform selection
    }
    if (campaignData.campaignType === 'proximity') {
      // ProximityIQ: 1-Location, 2-Type, 3-Configure, 4-Channel, 5-AudienceMethod, 6-Segments (quick) or CustomFilters, 7-Review, 8-Launch
      if (campaignData.audienceMethod === 'quick') return 8;
      if (campaignData.audienceMethod === 'custom') return 8;
      return 8;
    }
    return 5; // Default
  };

  // Check if location was pre-selected from agency portal, reseller flow, or client user
  useEffect(() => {
    if (location.state?.locationId) {
      // Set the pre-selected location and skip to step 2 (from agency portal)
      setCampaignData(prev => ({
        ...prev,
        selectedLocations: [location.state.locationId]
      }));
      setCurrentStep(2);
    } else if (location.state?.locationIds && location.state?.campaignType) {
      // Coming from reseller flow with client, locations, and campaign type pre-selected
      setCampaignData(prev => ({
        ...prev,
        selectedLocations: location.state.locationIds,
        campaignType: location.state.campaignType,
        clientId: location.state.clientId
      }));
      // Skip to step 3 (configure campaign) since type is already selected
      setCurrentStep(3);
    } else if (isClient) {
      // Client users don't have workspaces/locations - skip directly to campaign type
      setCurrentStep(2);
    }
  }, [location.state, isClient]);

  const updateCampaignData = useCallback((updates) => {
    // Merge metaAds instead of replacing it — in single-page mode several steps
    // sync at once, and replacing would let one step wipe another's fields
    setCampaignData(prev => ({
      ...prev,
      ...updates,
      ...(updates.metaAds && { metaAds: { ...prev.metaAds, ...updates.metaAds } }),
    }));
  }, []);

  // fromStep is the step whose Continue was clicked. In single-page mode every step is
  // mounted at once, so currentStep does not reflect which card the user is on.
  const handleNext = (fromStep = currentStep) => {
    console.log('handleNext called - From step:', fromStep);
    console.log('Total steps:', getTotalSteps());
    console.log('Campaign data:', campaignData);
    const nextStep = Math.min(fromStep + 1, getTotalSteps());
    console.log('Going to step:', nextStep);

    // If editing and this is the final step, save the campaign
    if (isEditing && nextStep > getTotalSteps()) {
      if (onSave) {
        onSave(campaignData);
      }
      return;
    }

    setCurrentStep(nextStep);

    if (singlePage) {
      // Scroll on every click (not in an effect on currentStep) so re-clicking a card still scrolls.
      // Wait a frame so a newly revealed card (e.g. after choosing a platform) is rendered first.
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-step="${nextStep}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  };


  const handleBack = () => {
    if (currentStep === 1) {
      // If editing, go back to campaigns dashboard
      if (isEditing) {
        navigate('/client/campaigns/new-camp-demo');
      } else {
        // Check if coming from agency flow
        if (window.location.pathname.includes('/agency/')) {
          navigate('/agency/campaigns');
        } else {
          navigate('/dashboard/client');
        }
      }
    } else if (currentStep === 2 && location.state?.locationId) {
      // If at step 2 and location was pre-selected from agency portal, go back to agency new campaign
      navigate('/agency/new-campaign');
    } else if (currentStep === 3 && location.state?.locationIds && location.state?.campaignType) {
      // If at step 3 and coming from reseller flow, go back to reseller campaign creation
      navigate('/whitelabel/campaigns/create');
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderStep = (step = currentStep) => {
    // Bind Continue to this card's step; ignore any args (e.g. a click event) steps might pass
    const goNext = () => handleNext(step);

    switch (step) {
      case 1:
        return (
          <SelectLocations
            selectedLocations={campaignData.selectedLocations}
            onUpdate={(locations) => updateCampaignData({ selectedLocations: locations })}
            onNext={goNext}
          />
        );
      case 2:
        return (
          <ChooseCampaignType
            selectedType={campaignData.campaignType}
            onUpdate={(type) => updateCampaignData({ campaignType: type })}
            onNext={goNext}
          />
        );
      case 3:
        if (campaignData.campaignType === 'stop') {
          return (
            <ConfigureStopLogic
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        if (campaignData.campaignType === 'route') {
          return (
            <ConfigureRouteLogic
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        if (campaignData.campaignType === 'proximity') {
          return (
            <ConfigureProximityIQ
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If campaignType not set yet, show campaign type selection
        return (
          <ChooseCampaignType
            selectedType={campaignData.campaignType}
            onUpdate={(type) => updateCampaignData({ campaignType: type })}
            onNext={goNext}
          />
        );
      case 4:
        if (campaignData.campaignType === 'stop') {
          return (
            <SelectStops
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        if (campaignData.campaignType === 'route') {
          return (
            <SelectRoutes
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        if (campaignData.campaignType === 'proximity') {
          return (
            <ChooseCampaignChannel
              selectedChannel={campaignData.campaignChannel}
              onUpdate={(channel) => updateCampaignData({ campaignChannel: channel })}
              onNext={goNext}
              isLastStep={false}
            />
          );
        }
        return null;
      case 5:
        if (campaignData.campaignType === 'proximity') {
          return (
            <SelectAudienceMethod
              selectedMethod={campaignData.audienceMethod}
              onUpdate={(method) => updateCampaignData({ audienceMethod: method })}
              onNext={goNext}
              channel={campaignData.campaignChannel}
            />
          );
        }
        return (
          <ChooseAdPlatform
            selectedPlatform={campaignData.adPlatform}
            onUpdate={(platform) => updateCampaignData({ adPlatform: platform })}
            onNext={goNext}
            campaignData={campaignData}
          />
        );
      case 6:
        if (campaignData.campaignType === 'proximity' && campaignData.audienceMethod === 'quick') {
          return (
            <SelectTargetSegments
              selectedSegments={campaignData.selectedSegments}
              onUpdate={updateCampaignData}
              onNext={goNext}
              channel={campaignData.campaignChannel}
              campaignData={campaignData}
            />
          );
        }
        if (campaignData.campaignType === 'proximity' && campaignData.audienceMethod === 'custom') {
          return (
            <BuildCustomAudience
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // Stop/Route campaign: Choose Campaign Objective (Meta - Step 6)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both')) {
          return (
            <ChooseCampaignObjective
              selectedObjective={campaignData.metaAds?.objective}
              onUpdate={(objective) => updateCampaignData({ 
                metaAds: { ...campaignData.metaAds, objective } 
              })}
              onNext={goNext}
            />
          );
        }
        // Stop/Route campaign: Configure Google Ads (if google only)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            campaignData.adPlatform === 'google') {
          return (
            <ConfigureGoogleAds
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 7:
        if (campaignData.campaignType === 'proximity') {
          // Route to SMS or Email template based on channel
          if (campaignData.campaignChannel === 'email') {
            return (
              <EmailTemplateDelivery
                campaignData={campaignData}
                onUpdate={updateCampaignData}
                onNext={goNext}
              />
            );
          }
          return (
            <SMSTemplateDelivery
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // Stop/Route campaign: Meta Campaign Settings (Meta - Step 7)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both')) {
          return (
            <MetaCampaignSettings
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // Stop/Route campaign: Review and Launch (Google only)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            campaignData.adPlatform === 'google') {
          return (
            <ReviewAndLaunch
              campaignData={campaignData}
            />
          );
        }
        return null;
      case 8:
        if (campaignData.campaignType === 'proximity') {
          return (
            <ReviewAndLaunch
              campaignData={campaignData}
            />
          );
        }
        // Stop/Route campaign: Meta Campaign Budget & Bid Strategy (Meta - Step 8)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both')) {
          return (
            <MetaCampaignBudgetBid
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 9:
        // Stop/Route campaign: Meta Ad Set Settings (Meta - Step 9)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both')) {
          return (
            <MetaAdSetSettings
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 10:
        // Stop/Route campaign: Meta Ad Set Budget & Schedule (Meta - Step 10, only when Advantage+ Campaign Budget is OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaAdSetBudgetSchedule
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If Advantage+ is ON, skip to Audience & Targeting
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaAudienceTargeting
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 11:
        // Stop/Route campaign: Meta Audience & Targeting (Meta - Step 11, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaAudienceTargeting
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If Advantage+ is ON, skip to Placements
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaPlacements
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 12:
        // Stop/Route campaign: Meta Placements (Meta - Step 12, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaPlacements
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If Advantage+ is ON, skip to Ad Format & Media
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaAdFormatMedia
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 13:
        // Stop/Route campaign: Meta Ad Format & Media (Meta - Step 13, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaAdFormatMedia
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If Advantage+ is ON, skip to Destination & Ad Copy
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') &&
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaDestinationAdCopy
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 14:
        // Stop/Route campaign: Meta Destination & Ad Copy (Meta - Step 14, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') &&
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaDestinationAdCopy
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If Advantage+ is ON, skip to Review & Launch
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') &&
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaReviewLaunch
              campaignData={campaignData}
            />
          );
        }
        return null;
      case 15:
        // Stop/Route campaign: Meta Review & Launch (Meta - Step 15, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') &&
            (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <MetaReviewLaunch
              campaignData={campaignData}
            />
          );
        }
        // If Advantage+ is ON and 'both' platform, configure Google Ads
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') &&
            campaignData.adPlatform === 'both' &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <ConfigureGoogleAds
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        return null;
      case 16:
        // Stop/Route campaign: Configure Google Ads (Both platforms - Step 16, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            campaignData.adPlatform === 'both' &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <ConfigureGoogleAds
              campaignData={campaignData}
              onUpdate={updateCampaignData}
              onNext={goNext}
            />
          );
        }
        // If Advantage+ is ON and 'both' platform, show final review
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            campaignData.adPlatform === 'both' &&
            campaignData.metaAds?.advantagePlusBudget) {
          return (
            <ReviewAndLaunch
              campaignData={campaignData}
            />
          );
        }
        return null;
      case 17:
        // Stop/Route campaign: Final Review & Launch (Both platforms - Step 17, only when Advantage+ OFF)
        if ((campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && 
            campaignData.adPlatform === 'both' &&
            !campaignData.metaAds?.advantagePlusBudget) {
          return (
            <ReviewAndLaunch
              campaignData={campaignData}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  const renderSinglePage = () => {
    const start = isClient ? 2 : 1;
    const total = getTotalSteps();
    const steps = [];

    if (!campaignData.campaignType) {
      steps.push(start);
      if (start === 1) steps.push(2);
    } else {
      for (let i = start; i <= total; i++) {
        steps.push(i);
      }
    }

    return (
      <div className="campaign-content" style={{ alignItems: 'stretch', paddingTop: 24, paddingBottom: 100, width: '100%', margin: 0 }}>
        {steps.map(step => (
          <div key={step} data-step={step} className="single-page-step" style={{ marginBottom: 24, padding: 32, width: '100%', background: 'var(--paper-50)', border: '1px solid var(--paper-300)', borderRadius: 24, boxShadow: 'var(--shadow-card)' }}>
            {renderStep(step)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="new-campaign-ux">
      {singlePage ? (
        renderSinglePage()
      ) : (
        <>
          {/* Header */}
          <div className="campaign-header">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeftOutlined />
              <span>{currentStep === 1 ? 'Back to Dashboard' : 'Back'}</span>
            </button>
            <div className="step-indicator">
              <span className="step-current">Step {currentStep}</span>
              <span className="step-total"> of {getTotalSteps()}</span>
            </div>
          </div>

          {/* Content */}
          <div className="campaign-content">
            {renderStep()}
          </div>
        </>
      )}
    </div>
  );
};

export default NewCampaignUX;
